import { useEffect, useState, type ReactNode } from "react";
import type { SessionAuthMethod, User } from "@ferrivia/api-contract";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useTranslation } from "@ferrivia/i18n";
import { AuthCallback } from "./AuthCallback";
import { EnrolPasskey, type EnrolmentReason } from "./EnrolPasskey";
import { useApi, usePasskeyDevice, useTracker } from "./AuthProvider";
import { useSession, type SessionCredentials } from "./useSession";
import s from "./DefaultNoAccess.module.css";

/** Fallback for `requireRole` mismatches when the app supplies no
    `renderNoAccess` — without this a wrong-role user hit a bare `null`
    forever. Generic on purpose: it has no app-specific copy to draw on. */
function DefaultNoAccess({ signOut }: { signOut: () => void }) {
  const { t } = useTranslation("auth");
  return (
    <Screen className={s.root}>
      <Text as="h1" size="xl" weight="bold">
        {t("noAccess.heading", "Ei käyttöoikeutta")}
      </Text>
      <Text as="p" className={s.description}>
        {t(
          "noAccess.description",
          "Kirjautuminen onnistui, mutta tällä tilillä ei ole käyttöoikeutta tähän sovellukseen.",
        )}
      </Text>
      <Button variant="secondary" onClick={signOut}>
        {t("noAccess.signOut", "Kirjaudu ulos")}
      </Button>
    </Screen>
  );
}

const DEFAULT_ENROLMENT_AUTH_METHODS: readonly SessionAuthMethod[] = ["eid"];

type PasskeyGateStatus = "checking" | "needed" | "satisfied";

export interface AuthGateProps {
  renderLogin: (p: {
    initialErrorCode: string | null;
    onSignedIn: (credentials: SessionCredentials) => void;
  }) => ReactNode;
  requireRole?: string;
  renderNoAccess?: (p: { signOut: () => void }) => ReactNode;
  children: (p: { user: User; signOut: () => void }) => ReactNode;
  /** Boot-splash dismissal for this gate's terminal branches. Left optional
      and app-supplied — the splash lives in apps/client, and this shared
      package cannot import app code. */
  onBootReady?: (reason: "login" | "enrolment") => void;
  /** Whether a cache-hydrated session (`trust: "cached"`) is good enough to
      render `children` before the real `/me` call verifies it. Refused
      whenever `requireRole` is set even if the caller passes `true` — role
      is an authorization input, and a stale cached role must never grant
      access. `apps/client` passes no `requireRole` today, but this gate is
      shared with the clinic app. */
  allowCachedSession?: boolean;
  /** Which kinds of session may be offered passkey enrolment. Must mirror the
      API's own gate for the deployment's market — the server enforces it, this
      only decides whether to ask. */
  passkeyEnrolmentAuthMethods?: readonly SessionAuthMethod[];
}

export function AuthGate({
  renderLogin,
  requireRole,
  renderNoAccess,
  children,
  onBootReady,
  allowCachedSession,
  passkeyEnrolmentAuthMethods = DEFAULT_ENROLMENT_AUTH_METHODS,
}: AuthGateProps) {
  const [onCallbackRoute, setOnCallbackRoute] = useState(
    () => window.location.pathname === "/auth/callback",
  );
  const [loginError, setLoginError] = useState<string | null>(null);
  const api = useApi();
  const tracker = useTracker();
  const passkeyDevice = usePasskeyDevice();
  const session = useSession();
  const [passkeyGate, setPasskeyGate] = useState<PasskeyGateStatus>("checking");
  const [enrolmentReason, setEnrolmentReason] = useState<EnrolmentReason>("no_passkey");

  // Enrolment is offered per *device*, not per account. A user whose account
  // already has a passkey on another device still has none on this one, and
  // asking the server "does this account have any passkey" answers the wrong
  // question — it is what left a second device (a new phone, a fresh PWA
  // install) silently un-enrolled forever. The account-level list still
  // matters in one direction: zero credentials means our local mark is
  // stale and the prompt is due again even on a device we thought was set up.
  // A cache-hydrated session is only good enough to render `children` with
  // when the caller opted in and there's no role to get wrong — see
  // `allowCachedSession` above.
  const cachedSessionUsable = Boolean(allowCachedSession) && !requireRole;
  const isAuthenticated =
    session.status === "authenticated" && (session.trust === "verified" || cachedSessionUsable);
  const user = isAuthenticated ? session.user : null;
  const userId = user && passkeyEnrolmentAuthMethods.includes(user.authMethod) ? user.id : undefined;
  // "offline"/"error" mean the boot fetch never resolved to a session either
  // way. There is no cached session to fall back on yet, so the only
  // non-blank thing to show is the login screen — better than the app
  // hanging forever, even though a signed-in user who's merely offline sees
  // a login prompt until they're back online.
  const showsLoginSlot =
    session.status === "anonymous" || session.status === "offline" || session.status === "error";

  useEffect(() => {
    if (!userId) {
      setPasskeyGate("satisfied");
      return;
    }
    // Cache-hydrated only, not yet verified: not safe to check the
    // account's passkeys over a network that might not exist. Wait for the
    // real /me to land instead of firing (and possibly failing) this.
    if (session.trust !== "verified") {
      return;
    }
    if (passkeyDevice.isSkipRecent(userId)) {
      setPasskeyGate("satisfied");
      return;
    }

    let cancelled = false;
    setPasskeyGate("checking");
    api.passkeys
      .list()
      .then((passkeys) => {
        if (cancelled) return;
        const enrolledHere = passkeyDevice.isEnrolled(userId);
        if (passkeys.length === 0) {
          // Nothing on the account at all, so nothing on this device either —
          // including whichever credential this browser was still naming in
          // allowCredentials, which can only fail now.
          if (enrolledHere) passkeyDevice.clearEnrolled(userId);
          passkeyDevice.forgetCredential();
          setEnrolmentReason("no_passkey");
          setPasskeyGate("needed");
          return;
        }
        setEnrolmentReason("new_device");
        setPasskeyGate(enrolledHere ? "satisfied" : "needed");
      })
      .catch(() => {
        if (cancelled) return;
        // Previously swallowed, which turned any blip on this one call into
        // "never ask about passkeys again". Fall back to what this browser
        // knows on its own, and leave a trace in the funnel either way.
        tracker.track("signin.enrolment_check_failed", {});
        setEnrolmentReason("check_failed");
        setPasskeyGate(passkeyDevice.isEnrolled(userId) ? "satisfied" : "needed");
      });
    return () => {
      cancelled = true;
    };
  }, [api, passkeyDevice, tracker, userId, session.trust]);

  // These are terminal branches, not loading states — the splash should
  // still be up while the gate is deciding (session "loading", passkey
  // "checking"), and comes down only once one of these actually renders.
  useEffect(() => {
    if (!onCallbackRoute && showsLoginSlot) {
      onBootReady?.("login");
    }
  }, [onCallbackRoute, showsLoginSlot, onBootReady]);

  useEffect(() => {
    if (passkeyGate === "needed" && userId) {
      onBootReady?.("enrolment");
    }
  }, [passkeyGate, userId, onBootReady]);

  function goHome() {
    setOnCallbackRoute(false);
  }

  if (onCallbackRoute) {
    return (
      <AuthCallback
        onSignedIn={(credentials) => {
          void session.signIn(credentials).then(goHome);
        }}
        onFailed={(errorCode) => {
          setLoginError(errorCode);
          goHome();
        }}
      />
    );
  }

  if (session.status === "loading") {
    return null;
  }

  if (showsLoginSlot) {
    return renderLogin({
      initialErrorCode: loginError,
      onSignedIn: (credentials) => void session.signIn(credentials),
    });
  }

  // Not authenticated for rendering purposes — either genuinely still
  // resolving, or authenticated-but-cached with no caller opted in to use
  // that yet. Either way, wait rather than guess: the splash stays up.
  if (!user) {
    return null;
  }

  // Passkey enrolment is a network check (and, if accepted, a mutation) —
  // gate it on a verified session only, same as the effect above. A
  // cache-hydrated shell renders `children` straight through; once trust
  // flips to "verified" this re-evaluates and may still prompt.
  if (session.trust === "verified") {
    if (passkeyGate === "needed" && userId) {
      return (
        <EnrolPasskey
          reason={enrolmentReason}
          onDone={() => {
            passkeyDevice.markEnrolled(userId);
            setPasskeyGate("satisfied");
          }}
          onSkip={() => {
            passkeyDevice.markSkipped(userId);
            setPasskeyGate("satisfied");
          }}
        />
      );
    }

    if (passkeyGate === "checking") {
      return null;
    }
  }

  if (requireRole && user.role !== requireRole) {
    const signOut = () => void session.signOut();
    return renderNoAccess ? renderNoAccess({ signOut }) : <DefaultNoAccess signOut={signOut} />;
  }

  return children({ user, signOut: () => void session.signOut() });
}
